// Pure helpers for the Surcharge Reconciliation page: labels for the miss
// classes (services/surcharge/reconciliation.py::MissClass), money/percent
// formatting, filtering and the CSV projections. Kept free of React so the
// page tests can cover behaviour without rendering.

import type { ExemptCustomerRow, OrderGroup, SurchargeRunSummary } from '../services/surchargeReconciliation';
import { toCsv } from '../utils/csv';

/** Order the classification tiles the way the report reads: money first. */
export const CLASS_ORDER: readonly string[] = [
  'collected',
  'removed_after_scan',
  'scanned_no_surcharge',
  'scanned_zero_at_site',
  'hand_keyed',
  'fuel_relabel',
  'partial',
  'exempt_customer',
  'exempt_quote',
  'over_billed',
  'reversed_by_cm',
  'outside_program_dates',
  'house',
];

export const CLASS_LABELS: Record<string, string> = {
  collected: 'Collected in full',
  removed_after_scan: 'Removed in Sage after scan',
  scanned_no_surcharge: 'Scanned, no surcharge',
  scanned_zero_at_site: 'Site computed $0 at scan',
  hand_keyed: 'Hand-keyed in Sage',
  fuel_relabel: 'Relabelled as /FUEL',
  partial: 'Partially collected',
  exempt_customer: 'Exempt customer',
  exempt_quote: 'Exempt quote',
  over_billed: 'Over-billed',
  reversed_by_cm: 'Reversed by credit memo',
  outside_program_dates: 'Outside program dates',
  house: 'House account',
};

/** Classes whose gap is real money we did not collect. */
export const SHORTFALL_CLASSES: ReadonlySet<string> = new Set([
  'removed_after_scan',
  'scanned_no_surcharge',
  'hand_keyed',
  'fuel_relabel',
  'partial',
]);

export function classLabel(key: string): string {
  return CLASS_LABELS[key] ?? key.replace(/_/g, ' ');
}

export function money(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

export function pct(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  return Number.isNaN(n) ? String(value) : `${n.toFixed(1)}%`;
}

export function runLabel(run: SurchargeRunSummary): string {
  const when = new Date(run.started_at);
  const stamp = Number.isNaN(when.getTime()) ? run.started_at : when.toLocaleString();
  return `${run.window_start} → ${run.window_end} · ${run.trigger} · ${stamp}${run.status === 'completed' ? '' : ` (${run.status})`}`;
}

export interface GroupFilters {
  classification: string | '';
  salesperson: string | '';
  q: string;
}

export const DEFAULT_GROUP_FILTERS: GroupFilters = { classification: '', salesperson: '', q: '' };

export function filterGroups(groups: OrderGroup[], f: GroupFilters): OrderGroup[] {
  const q = f.q.trim().toLowerCase();
  return groups.filter((g) => {
    if (f.classification && g.classification !== f.classification) return false;
    if (f.salesperson && (g.salesperson_no ?? '') !== f.salesperson) return false;
    if (q) {
      const hay = [
        g.customer_no,
        g.customer_name ?? '',
        g.customer_po_no,
        ...g.sales_order_nos,
        ...g.invoice_nos,
        g.note,
      ]
        .join(' ')
        .toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
}

/** Largest gap first, then by goods; ties by customer. */
export function sortGroups(groups: OrderGroup[]): OrderGroup[] {
  return [...groups].sort((a, b) => {
    const gap = Number(b.gap) - Number(a.gap);
    if (gap !== 0) return gap;
    const goods = Number(b.goods) - Number(a.goods);
    if (goods !== 0) return goods;
    return a.customer_no.localeCompare(b.customer_no);
  });
}

export function distinctSalespeople(groups: OrderGroup[]): { no: string; name: string }[] {
  const seen = new Map<string, string>();
  for (const g of groups) {
    const no = g.salesperson_no ?? '';
    if (!no) continue;
    if (!seen.has(no)) seen.set(no, g.salesperson_name ?? no);
  }
  return [...seen.entries()]
    .map(([no, name]) => ({ no, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export function groupsToCsv(groups: OrderGroup[]): string {
  return toCsv(
    [
      'customer_no', 'customer_name', 'customer_po_no', 'sales_order_nos', 'invoice_nos', 'first_invoice_date',
      'salesperson', 'goods', 'expected_policy', 'queued_at_scan', 'billed_surcharge', 'gap', 'classification', 'note',
      'bol_scanned', 'customer_exempt_now', 'exempt_quote_ids', 'exemption_basis',
    ],
    groups.map((g) => [
      g.customer_no, g.customer_name, g.customer_po_no, g.sales_order_nos.join(' '), g.invoice_nos.join(' '),
      g.first_invoice_date, g.salesperson_name ?? g.salesperson_no, g.goods, g.expected_policy, g.queued_surcharge ?? '',
      g.billed_surcharge, g.gap, classLabel(g.classification), g.note, g.bol_scanned === null ? '' : String(g.bol_scanned),
      String(g.customer_exempt_now), g.exempt_quote_ids.join(' '), g.exemption_basis,
    ]),
  );
}

export function exemptCustomersToCsv(rows: ExemptCustomerRow[]): string {
  return toCsv(
    ['customer_no', 'customer_name', 'status', 'salesperson', 'is_exempt', 'window_goods', 'window_invoices', 'surcharge_billed', 'surcharge_foregone'],
    rows.map((r) => [
      r.customer_no, r.customer_name, r.customer_status, r.salesperson_name ?? r.salesperson_no, String(r.is_exempt),
      r.window_goods, r.window_invoice_count, r.window_surcharge_billed, r.window_surcharge_foregone,
    ]),
  );
}

/** Exempt customers that actually bought in the window, biggest foregone first. */
export function activeExemptCustomers(rows: ExemptCustomerRow[]): ExemptCustomerRow[] {
  return rows
    .filter((r) => r.window_invoice_count > 0 || Number(r.window_surcharge_foregone) > 0)
    .sort((a, b) => Number(b.window_surcharge_foregone) - Number(a.window_surcharge_foregone));
}
